import { Link } from "react-router-dom";
import { dateFunc } from "@/components/Functions/dateFunc";
import {
  customComparator,
  formatAmountUnitDecimalPlace,
} from "@/utility/common";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const useDefaultUnitDetails = () => {
  const { defaultUnit } = useSelector((state: any) => state.case);
  const decimalPlace = defaultUnit?.decimalPlace;
  const defaultUnitName = defaultUnit?.name;
  return { decimalPlace, defaultUnitName };
};

const CustomHeader = () => {
  const { defaultUnitName } = useDefaultUnitDetails();
  const unitName = defaultUnitName;
  return `(${unitName})`;
};

const amountCellRenderer = (field: string) => (params: any) => {
  const { decimalPlace, defaultUnitName } = useDefaultUnitDetails();
  const amount = params.data[field];
  const formattedDefaultAmount = formatAmountUnitDecimalPlace(
    amount,
    decimalPlace
  );
  return formattedDefaultAmount !== "-"
    ? `${formattedDefaultAmount} ${defaultUnitName}`
    : formattedDefaultAmount;
};

export const useColumnDefs = ({
  caseId,
  creditorId,
  abbreviation,
}: {
  caseId: string | undefined;
  creditorId: string | undefined;
  abbreviation: string | undefined;
}) => {
  const { t } = useTranslation();
  return [
    {
      cellRenderer: "claimCellRenderer",
      field: "claimReferenceId",
      type: "claimReferenceIdType",
      headerName: t(
        "pages.creditors.creditorClaimTab.tableColumnsDetails.claimId"
      ),
      headercomponentParams: { caseId, creditorId },
      flex: 1,
      minWidth: 150,
      sortable: true,
      unSortIcon: true,
    },
    {
      cellRenderer: "debtorNameCellRenderer",
      field: "debtorName",
      type: "debtorNameType",
      headerName: t(
        "pages.creditors.creditorClaimTab.tableColumnsDetails.debtorName"
      ),
      wrapText: true,
      autoHeight: true,
      flex: 1,
      minWidth: 150,
      sortable: true,
      unSortIcon: true,
    },
    {
      cellRenderer: "creditorNameCellRenderer",
      field: "creditorName",
      type: "creditorNameType",
      headerName: t(
        "pages.creditors.creditorClaimTab.tableColumnsDetails.creditorName"
      ),
      wrapText: true,
      autoHeight: true,
      flex: 1,
      minWidth: 150,
      sortable: true,
      unSortIcon: true,
    },
    {
      cellRenderer: "assigneeNameCellRenderer",

      field: "assigneeName",
      type: "assigneeNameType",
      headerName: t(
        "pages.creditors.creditorClaimTab.tableColumnsDetails.assignee"
      ),
      flex: 1,
      minWidth: 150,
      sortable: true,
      unSortIcon: true,
      wrapText: true,
      autoHeight: true,
    },
    {
      cellRenderer: amountCellRenderer("unresolvedCreditorAmount"),
      field: "unresolvedCreditorAmount",
      type: "unresolvedCreditorAmountType",
      headerName: t(
        "pages.creditors.creditorClaimTab.tableColumnsDetails.unResolvedCreditorAmount",
        { unitName: CustomHeader() }
      ),
      flex: 1,
      minWidth: 180,
      // headerHeight: 150,
      sortable: true,
      unSortIcon: true,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
    },
    {
      cellRenderer: amountCellRenderer("unresolvedDebtorAmount"),
      field: "unresolvedDebtorAmount",
      type: "unresolvedDebtorAmountType",
      headerName: t(
        "pages.creditors.creditorClaimTab.tableColumnsDetails.unResolvedDebtorAmount",
        { unitName: CustomHeader() }
      ),
      flex: 1,
      minWidth: 180,
      // headerHeight: 150,
      sortable: true,
      unSortIcon: true,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
    },
    {
      cellRenderer: amountCellRenderer("resolvedAmount"),
      field: "resolvedAmount",
      type: "resolvedAmountType",
      headerName: t(
        "pages.creditors.creditorClaimTab.tableColumnsDetails.resolvedAmount",
        { unitName: CustomHeader() }
      ),
      flex: 1,
      minWidth: 180,
      // headerHeight: 150,
      sortable: true,
      unSortIcon: true,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
    },
    {
      cellRenderer: amountCellRenderer("originalAmount"),
      field: "originalAmount",
      type: "originalAmountType",
      headerName: t(
        "pages.creditors.creditorClaimTab.tableColumnsDetails.originalAmount",
        { unitName: CustomHeader() }
      ),
      flex: 1,
      minWidth: 180,
      // headerHeight: 150,
      sortable: true,
      unSortIcon: true,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
    },
    {
      cellRenderer: amountCellRenderer("currentAmount"),
      field: "currentAmount",
      type: "currentAmountType",
      headerName: t(
        "pages.creditors.creditorClaimTab.tableColumnsDetails.currentAmount",
        { unitName: CustomHeader() }
      ),
      flex: 1,
      minWidth: 180,
      // headerHeight: 150,
      sortable: true,
      unSortIcon: true,
      cellClass: "align-right",
      headerClass: "stepHeaderClass",
    },
    {
      field: "claimStageName",
      type: "claimStageNameType",
      headerName: t(
        "pages.creditors.creditorClaimTab.tableColumnsDetails.Stage"
      ),
      flex: 1,
      minWidth: 150,
      sortable: true,
      comparator: customComparator,
      unSortIcon: true,
    },
    {
      field: "actionStatusName",
      type: "actionStatusNameType",
      headerName: t(
        "pages.creditors.creditorClaimTab.tableColumnsDetails.actionStatus"
      ),
      flex: 1,
      minWidth: 150,
      sortable: true,
      comparator: customComparator,
      unSortIcon: true,
    },
    {
      field: "updatedOn",
      type: "updatedOnType",
      headerName: t(
        "pages.creditors.creditorClaimTab.tableColumnsDetails.lastUpdated"
      ),
      flex: 1,
      minWidth: 200,
      sortable: true,
      unSortIcon: true,
      cellRenderer: function (param: any) {
        return (
          <>
            {dateFunc(param.data.updatedOn).formattedDateTime +
              " " +
              abbreviation}
          </>
        );
      },
    },
  ];
};

type documentConst = {
  data: {
    documentType: string;
    comment: string;
    createdBy: string;
    createdOn: string;
  };
};

//Column Defination for document table in Creditor summary
export const useColumnDefsCreditor = (abbreviation: string) => {
  const { t } = useTranslation();
  return [
    {
      minWidth: 50,
      flex: 1,
      field: "downloadAction",
      headerName: "",
      cellRenderer: "downloadActionRenderer",
      cellStyle: { justifyContent: "center" },
    },
    {
      cellRenderer: "displayNameRenderer",
      flex: 1,
      minWidth: 200,
      field: "displayName",
      headerName: t(
        "pages.creditors.creditorDocumentUpload.tableColumnsDetails.displayName"
      ),
      unSortIcon: true,
      autoHeight: true,
      cellClass: "text-bold-underline grid-text-multiline",
      cellStyle: {
        "white-space": "normal",
        "line-height": "2",
        "word-break": "break-word",
      },
    },
    {
      cellRenderer: "typeCellRenderer",
      flex: 1,
      minWidth: 200,

      field: "documentType",
      headerName: t(
        "pages.creditors.creditorDocumentUpload.tableColumnsDetails.type"
      ),
      unSortIcon: true,
    },
    {
      flex: 1,
      minWidth: 200,

      field: "comment",
      headerName: t(
        "pages.creditors.creditorDocumentUpload.tableColumnsDetails.comment"
      ),
      unSortIcon: true,
      autoHeight: true,
      cellClass: "text-bold-underline grid-text-multiline",
      cellStyle: {
        "white-space": "normal",
        "line-height": "2",
        "word-break": "break-word",
      },
    },
    {
      flex: 1,
      minWidth: 200,

      field: "createdBy",
      headerName: t(
        "pages.creditors.creditorDocumentUpload.tableColumnsDetails.createdBy"
      ),
      unSortIcon: true,
    },
    {
      flex: 1,
      minWidth: 200,

      field: "createdOn",
      unSortIcon: true,
      sort:"desc",
      headerName: t(
        "pages.creditors.creditorDocumentUpload.tableColumnsDetails.receivedDate"
      ),
      cellRenderer: function (params: documentConst) {
        return (
          dateFunc(params.data.createdOn).formattedDateTime + " " + abbreviation
        );
      },
    },
    {
      flex: 1,
      minWidth: 200,

      headerName: t(
        "pages.creditors.creditorDocumentUpload.tableColumnsDetails.claimId"
      ),
      field: "claimReference",
      unSortIcon: true,
      cellRenderer: (params: any) => {
        if (params.value) {
          const { caseId, creditorId, entityId } = params.data;
          let prefix = `/case/${caseId}/creditor/${creditorId}`;
          return (
            <Link
              to={`${prefix}/claim/${entityId}`}
              state={{ from: params.data.claimId }}
            >
              <span className="text-bold-underline"> {params.value}</span>
            </Link>
          );
        }
        return null;
      },
    },
    {
      minWidth: 50,
      flex: 1,
      field: "action",
      headerName: "",
      cellRenderer: "actionRenderer",
      cellStyle: { justifyContent: "center" },
    },
  ];
};
